export const METRICS = [
  {
    key: 'proPMw',
    label: 'Production Power (W)',
    shortLabel: 'Pro P',
    unit: 'W',
    field: 'power',
    color: '#06b6d4',
    divisor: 1000,
  },
  {
    key: 'conPMw',
    label: 'Consumption Power (W)',
    shortLabel: 'Con P',
    unit: 'W',
    field: 'power',
    color: '#f97316',
    divisor: 1000,
    negateValue: true,
  },
  {
    key: 'netBattery',
    label: 'Net Battery (W)',
    shortLabel: 'Battery',
    unit: 'W',
    color: '#84cc16',
    netBattery: true,
  },
  {
    key: 'chaEWh_avg',
    label: 'Avg Charge Power (W)',
    shortLabel: 'Avg Charge',
    unit: 'W',
    color: '#65a30d',
    derived: true,
    energyKey: 'chaEWh',
    energyField: 'energy',
  },
  {
    key: 'disEWh_avg',
    label: 'Avg Discharge Power (W)',
    shortLabel: 'Avg Discharge',
    unit: 'W',
    color: '#65a30d',
    derived: true,
    energyKey: 'disEWh',
    energyField: 'energy',
  },
  {
    key: 'gridPMw',
    label: 'Grid Real Power (W)',
    shortLabel: 'Grid P',
    unit: 'W',
    field: 'power',
    color: '#6b7280',
    divisor: 1000,
  },
  {
    key: 'gridCMa',
    label: 'Grid Current (A)',
    shortLabel: 'Grid I',
    unit: 'A',
    field: 'current',
    color: '#a855f7',
    divisor: 1000,
  },
  {
    key: 'gridVMv',
    label: 'Grid Voltage (V)',
    shortLabel: 'Grid V',
    unit: 'V',
    field: 'voltage',
    color: '#8b5cf6',
    divisor: 1000,
  },
  {
    key: 'gridSMva',
    label: 'Grid Apparent Power (VA)',
    shortLabel: 'Grid S',
    unit: 'VA',
    field: 'power',
    color: '#ec4899',
    signed: true,
    divisor: 1000,
  },
  {
    key: 'gridPf',
    label: 'Power Factor',
    shortLabel: 'PF',
    unit: '',
    field: 'powerFactor',
    color: '#eab308',
    divisor: 1000,
  },
  {
    key: 'socPer',
    label: 'Battery SOC (%)',
    shortLabel: 'SOC',
    unit: '%',
    color: '#10b981',
    scalar: true,
  },
]

export const TIME_RANGES = [
  { label: 'Last 5 min', minutes: 5 },
  { label: 'Last 15 min', minutes: 15 },
  { label: 'Last 30 min', minutes: 30 },
  { label: 'Last 1 hour', minutes: 60 },
  { label: 'Last 3 hours', minutes: 180 },
  { label: 'Custom', minutes: null },
]

export const EVENT_CONFIG = {
  STATE_TRANSITION: { icon: '\u25CF', color: '#2563eb', label: 'Transition' },
  COMMAND_ISSUED:   { icon: '\u26A1', color: '#059669', label: 'Command' },
  COMMAND_FAILED:   { icon: '\u2716', color: '#dc2626', label: 'Failed' },
  EXIT_PENDING:     { icon: '\u25CB', color: '#d97706', label: 'Exit Pending' },
}

export const STATE_COLORS = {
  WATCHING:   { bg: '#e5e7eb', border: '#9ca3af', text: '#6b7280' },
  MODULATING: { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
}

export function formatTs(isoStr) {
  const date = new Date(isoStr)
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
}

export function formatTsFull(isoStr) {
  const date = new Date(isoStr)
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  })
}

export function extractDischargeWatts(event) {
  const snap = event.digital_twin_snapshot
  if (!snap) return null
  const cmd = snap.last_issued_command || snap.lastIssuedCommand
  if (!cmd) return null
  return cmd.discharge_power_watts ?? cmd.dischargePowerWatts ?? null
}

export function deriveStatePeriods(events, timeWindowFrom, timeWindowTo) {
  if (!events || events.length === 0) return []
  const sorted = [...events].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))

  const rawPeriods = []
  let currentState = sorted[0].from_state || 'WATCHING'
  let periodStart = timeWindowFrom
  for (const event of sorted) {
    if (event.event_type === 'STATE_TRANSITION' && event.to_state !== currentState) {
      rawPeriods.push({ state: currentState, from: periodStart, to: new Date(event.timestamp) })
      currentState = event.to_state
      periodStart = new Date(event.timestamp)
    }
  }
  rawPeriods.push({ state: currentState, from: periodStart, to: timeWindowTo })

  const TICK_MS = 5000
  const periods = []
  for (const period of rawPeriods) {
    if (period.state !== 'MODULATING') {
      periods.push(period)
      continue
    }
    const periodFromMs = period.from.getTime()
    const periodToMs = period.to.getTime()
    const exitPendingEvents = sorted.filter(
      (event) => event.event_type === 'EXIT_PENDING' &&
        new Date(event.timestamp).getTime() >= periodFromMs &&
        new Date(event.timestamp).getTime() <= periodToMs
    )
    if (exitPendingEvents.length === 0) {
      periods.push(period)
      continue
    }
    const allEventsInPeriod = sorted.filter(
      (event) => new Date(event.timestamp).getTime() >= periodFromMs &&
        new Date(event.timestamp).getTime() <= periodToMs
    )
    let subStart = period.from
    let inExitBlock = false
    let exitBlockStart = null
    let lastExitPendingTime = null
    for (const event of allEventsInPeriod) {
      if (event.event_type === 'EXIT_PENDING') {
        const eventTime = new Date(event.timestamp)
        if (!inExitBlock) {
          if (eventTime.getTime() > subStart.getTime()) {
            periods.push({ state: 'MODULATING', from: subStart, to: eventTime })
          }
          exitBlockStart = eventTime
          inExitBlock = true
        }
        lastExitPendingTime = eventTime
      } else if (inExitBlock) {
        const cappedEnd = new Date(Math.min(lastExitPendingTime.getTime() + TICK_MS, period.to.getTime()))
        periods.push({ state: 'EXIT_PENDING', from: exitBlockStart, to: cappedEnd })
        const eventTime = new Date(event.timestamp)
        subStart = cappedEnd.getTime() < eventTime.getTime() ? cappedEnd : eventTime
        inExitBlock = false
        lastExitPendingTime = null
      }
    }
    if (inExitBlock) {
      const cappedEnd = new Date(Math.min(lastExitPendingTime.getTime() + TICK_MS, period.to.getTime()))
      periods.push({ state: 'EXIT_PENDING', from: exitBlockStart, to: cappedEnd })
      if (cappedEnd.getTime() < period.to.getTime()) {
        periods.push({ state: 'MODULATING', from: cappedEnd, to: period.to })
      }
    } else if (subStart.getTime() < period.to.getTime()) {
      periods.push({ state: 'MODULATING', from: subStart, to: period.to })
    }
  }
  return periods
}
